import Prismic from 'prismic-javascript'

import Router from '@/router'

import NotificationService from '@/services/notification'
import Logger from '@/services/logger'

import PrismicClient from '@/helpers/prismic-client'
import i18n from '@/helpers/i18n'

import { LANG_FR, LANG_EN } from '@/consts/langs'
import { URL_STATIC_PAGES } from '@/consts/urls'

const PRISMIC_EN_LOCALE = 'en-ca'
const PRISMIC_FR_LOCALE = 'fr-ca'

const DEFAULT_OPTIONS = { cache: true }

function getCurrentPrismicLocale() {
  var appLocale = i18n.locale()
  switch (appLocale) {
    case LANG_FR:
      return PRISMIC_FR_LOCALE
    case LANG_EN:
      return PRISMIC_EN_LOCALE
  }
}

async function query(predicates) {
  var result = null
  try {
    var allData = await PrismicClient.get().query(predicates, { lang: getCurrentPrismicLocale() })
    result = allData.results
  } catch (err) {
    NotificationService.showError(i18n.t('cms-error-notification'))
    Logger.logError(err)
  }

  return result
}

export default {
  currentLocale: getCurrentPrismicLocale,

  linkResolver: function(doc) {
    /* Si jamais on veut générer des <router-link> pour que la navigation soit plus smooth, 
       il faudrait implémenter un HTML Serializer custom.
       Voir: https://prismic.io/docs/vuejs/beyond-the-api/html-serializer */

    const resolvedRoute = Router.resolve({
      name: URL_STATIC_PAGES,
      params: {
        uid: doc.uid
      }
    })

    return resolvedRoute.href
  },

  async getFirstPageByDocumentType(documentType, options) {
    const opts = { ...DEFAULT_OPTIONS, ...options }
    const language = getCurrentPrismicLocale()
    const key = `getFirstPageByDocumentType-${documentType}-${language}`
    const resolver = () => this.getPagesByDocumentType(documentType)

    const result = opts.cache ? await _getCachedContent.getContent(key, resolver) : await resolver()
    return result[0]
  },

  async getPagesByDocumentType(documentType, options) {
    const opts = { ...DEFAULT_OPTIONS, ...options }
    const language = getCurrentPrismicLocale()
    const key = `getPagesByDocumentType-${documentType}-${language}`
    const resolver = () => query([Prismic.Predicates.at('document.type', documentType)])

    const result = opts.cache ? await _getCachedContent(key, resolver) : await resolver()
    return result
  },

  async getFirstPageByPredicates(predicates, options) {
    const opts = { ...DEFAULT_OPTIONS, ...options }
    const language = getCurrentPrismicLocale()
    const key = `getFirstPageByPredicates-${JSON.stringify(predicates)}-${language}`
    const resolver = () => query(predicates)

    const result = opts.cache ? await _getCachedContent(key, resolver) : await resolver()
    return result[0]
  }
}

const _requestsCache = new Map()
const _cacheDurationInSec = 1000 * 60 * 5 // 5 minutes
const _getCachedContent = async (key, resolver) => {
  try {
    var promise = _getPromise(key, resolver)
    return await promise.then(
      result => {
        return result
      },
      error => {
        throw error
      }
    )
  } catch (err) {
    // eslint-disable-next-line
    console.warn('Unable to resolve promise in cache. Falling back to no-cache', err)
    return await resolver()
  }
}
const _getPromise = async (key, resolver) => {
  // Get promise in cache
  const cachedValue = _requestsCache.get(key)
  if (cachedValue) return cachedValue

  const promise = new Promise(async (resolve, reject) => {
    try {
      const result = await resolver()
      resolve(result)
    } catch (err) {
      reject(err)
    }
  })

  // Add promise to cache
  _requestsCache.set(key, promise)

  // Schedule remove from cache
  setTimeout(() => _requestsCache.delete(key), _cacheDurationInSec)

  return promise
}
